import { BrandingProvider } from '@/providers/branding/branding.provider';
import { CognitoProvider } from '@/providers/cognito.provider';
import { isNotNullish } from '@/utils/typing/is-nullish.predicate';
import { inject, Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { firstValueFrom, type Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import type { CoreApiToken } from '../../../shared';
import { SsoAuthenticationSessionStorageKey } from '../../constants/authentication-session-storage-key.enum';
import { RosaCoreLocalStorageKey } from '../../constants/core-local-storage-key.enum';
import { RosaAuthStoreActions, RosaAuthStoreSelectors } from '../../store/auth';
import type { RosaCoreState } from '../../store/core.state';
import { RosaCoreApiService } from '../core-api.service';
import { RosaStoreService } from '../store.service';
import { generateSessionId, sessionId, type SessionId } from './auth.interface';
import { RosaCoreApiAuthService } from './core-api-auth.service';

@Injectable()
export class RosaAuthService {
    private _store = inject<Store<RosaCoreState>>(Store);
    private _coreApiAuthService = inject(RosaCoreApiAuthService);
    private _coreApiService = inject(RosaCoreApiService);
    private _storeService = inject(RosaStoreService);
    private _brandingProvider = inject(BrandingProvider);
    private _cognitoProvider = inject(CognitoProvider);

    isAuthenticated$(): Observable<boolean> {
        return this._store.pipe(
            filter(store => !store.auth.isPending),
            map(store => store.auth.isAuthenticated)
        );
    }

    isAuthenticated(): boolean {
        return this._storeService.getStoreInstantValue(RosaAuthStoreSelectors.selectIsAuthenticated);
    }

    isAuthenticatedWithSso(): boolean {
        return localStorage.getItem(RosaCoreLocalStorageKey.CONNECTED_WITH_SSO) === 'true';
    }

    isPending$(): Observable<boolean> {
        return this._store.pipe(select(RosaAuthStoreSelectors.selectIsPending));
    }

    areGeneralConditionAccepted$(): Observable<boolean> {
        return this._store.pipe(select(RosaAuthStoreSelectors.selectIsCguAccepted), filter(isNotNullish));
    }

    isPasswordExpired$(): Observable<boolean> {
        return this._store.pipe(select(RosaAuthStoreSelectors.selectIsPasswordExpired));
    }

    isConnectedAs$(): Observable<boolean> {
        return this._store.pipe(select(RosaAuthStoreSelectors.selectIsConnectedAs));
    }

    isConnectedAs(): boolean {
        return this._storeService.getStoreInstantValue(RosaAuthStoreSelectors.selectIsConnectedAs);
    }

    getToken$(): Observable<CoreApiToken | null> {
        return this._store.pipe(select(RosaAuthStoreSelectors.selectToken));
    }

    getToken(): CoreApiToken | null {
        return this._storeService.getStoreInstantValue(RosaAuthStoreSelectors.selectToken);
    }

    getSessionId(): SessionId | null {
        return this._storeService.getStoreInstantValue(RosaAuthStoreSelectors.selectSessionId);
    }

    /**
     * This session id should probably be managed by a future auth service
     */
    private readOrCreateSessionId(): SessionId {
        const storedSessionId = sessionStorage.getItem(RosaCoreLocalStorageKey.SESSION_ID);
        return storedSessionId ? sessionId(storedSessionId) : generateSessionId();
    }

    private deleteSessionId() {
        sessionStorage.removeItem(RosaCoreLocalStorageKey.SESSION_ID);
    }

    login(email: string, password: string): Promise<CoreApiToken> {
        this._store.dispatch(new RosaAuthStoreActions.LoginRequest());
        return firstValueFrom(this._coreApiAuthService.getToken(email, password))
            .then(token => {
                this.registerToken(token, this.readOrCreateSessionId());
                return token;
            })
            .catch(error => {
                this._store.dispatch(new RosaAuthStoreActions.LoginFailed());
                throw error;
            });
    }

    async autologin(reloginAsPrompt?: (email: string) => Promise<boolean>): Promise<CoreApiToken> {
        const idpToken = sessionStorage.getItem(SsoAuthenticationSessionStorageKey.IDP_TOKEN);
        sessionStorage.removeItem(SsoAuthenticationSessionStorageKey.IDP_TOKEN);
        const idpCode = sessionStorage.getItem(SsoAuthenticationSessionStorageKey.IDP_CODE);
        sessionStorage.removeItem(SsoAuthenticationSessionStorageKey.IDP_CODE);

        const codeVerifier = sessionStorage.getItem(SsoAuthenticationSessionStorageKey.CODE_VERIFIER);
        sessionStorage.removeItem(SsoAuthenticationSessionStorageKey.CODE_VERIFIER);

        const refreshToken = localStorage.getItem(RosaCoreLocalStorageKey.REFRESH_TOKEN);

        if (!refreshToken && !idpToken && !idpCode) {
            this._store.dispatch(new RosaAuthStoreActions.LoginFailed());
            throw new Error('NO_REFRESH_TOKEN_STORED');
        }

        this._store.dispatch(new RosaAuthStoreActions.LoginRequest());

        let coreApiToken: Promise<CoreApiToken>;
        if (idpToken !== null || idpCode !== null) {
            const branding = this._brandingProvider.getBranding();
            if (!branding?.idpCode) {
                this._store.dispatch(new RosaAuthStoreActions.LoginFailed());
                throw new Error('Error cannot connect to SSO with null brand');
            }

            coreApiToken = this._cognitoProvider.convertIdpAccessTokenToAdvizeoAuthToken(idpToken, idpCode, branding.idpCode, codeVerifier);
        } else {
            coreApiToken = firstValueFrom(this._coreApiService.getNewTokenFromRefresh$(refreshToken as string));
        }

        const storedConnectedAsEmail = localStorage.getItem(RosaCoreLocalStorageKey.CONNECTED_AS_EMAIL);
        if (storedConnectedAsEmail && reloginAsPrompt) {
            const shouldRelogAs = await reloginAsPrompt(storedConnectedAsEmail);
            if (shouldRelogAs) {
                coreApiToken = coreApiToken.then(token => this.reloginAs(token, storedConnectedAsEmail));
            } else {
                localStorage.removeItem(RosaCoreLocalStorageKey.CONNECTED_AS_EMAIL);
                this.deleteSessionId();
            }
        }

        return coreApiToken
            .then(token => {
                this.registerToken(token, this.readOrCreateSessionId());
                return token;
            })
            .catch(error => {
                console.error('An error occured while trying to autologin. Removing stored credentials...');
                this._store.dispatch(new RosaAuthStoreActions.LoginFailed());
                sessionStorage.removeItem(SsoAuthenticationSessionStorageKey.IDP_TOKEN);
                sessionStorage.removeItem(SsoAuthenticationSessionStorageKey.IDP_CODE);
                localStorage.removeItem(RosaCoreLocalStorageKey.REFRESH_TOKEN);
                localStorage.removeItem(RosaCoreLocalStorageKey.CONNECTED_WITH_SSO);
                localStorage.removeItem(RosaCoreLocalStorageKey.CONNECTED_AS_EMAIL);
                localStorage.removeItem(RosaCoreLocalStorageKey.CONNECTED_AS_PROJECT_ID);
                this.deleteSessionId();
                throw error;
            });
    }

    loginAs(email: string, projectId?: string): Promise<CoreApiToken> {
        return this._coreApiAuthService.getTokenForConnectedAs(email, projectId).then(token => {
            this.registerConnectedAs(email);
            this.registerToken(token, generateSessionId());
            return token;
        });
    }

    backToMainAccount(): Promise<CoreApiToken> {
        localStorage.removeItem(RosaCoreLocalStorageKey.CONNECTED_AS_EMAIL);
        localStorage.removeItem(RosaCoreLocalStorageKey.CONNECTED_AS_PROJECT_ID);

        this.deleteSessionId();
        this._store.dispatch(new RosaAuthStoreActions.SetIsConnectedAs(false));

        if (localStorage.getItem(RosaCoreLocalStorageKey.CONNECTED_WITH_SSO)) {
            this._brandingProvider.connectWithSso();
            return new Promise<never>(() => {}); // avoid unwanted effect after redirection
        }
        return this.autologin();
    }

    logout(): void {
        localStorage.removeItem(RosaCoreLocalStorageKey.CONNECTED_AS_EMAIL);
        localStorage.removeItem(RosaCoreLocalStorageKey.CONNECTED_AS_PROJECT_ID);
        localStorage.removeItem(RosaCoreLocalStorageKey.REFRESH_TOKEN);
        localStorage.removeItem(RosaCoreLocalStorageKey.CONNECTED_WITH_SSO);
        localStorage.removeItem(RosaCoreLocalStorageKey.DEVELOPER_HELPER);
        this.deleteSessionId();
        this._store.dispatch(new RosaAuthStoreActions.Logout());
    }

    registerToken(token: CoreApiToken, sessionId: SessionId): void {
        this._coreApiService.storeToken(token, sessionId);
        this._store.dispatch(new RosaAuthStoreActions.LoginSucceeded());
    }

    registerConnectedAs(targetEmail: string): void {
        this._store.dispatch(new RosaAuthStoreActions.SetIsConnectedAs(true));
        localStorage.setItem(RosaCoreLocalStorageKey.CONNECTED_AS_EMAIL, targetEmail);
    }

    changeProject(projectId: string): Promise<CoreApiToken> {
        return this._coreApiAuthService.switchProjectInToken(projectId).then(token => {
            this._coreApiService.storeToken(token, generateSessionId());
            return token;
        });
    }

    /**
     * Re-mints a fresh token from the current access token (no stored credentials needed).
     * Useful after a server-side change (CGU acceptance, password renewal) that must be
     * reflected in the token, notably for SSO sessions that have no refresh token.
     */
    refreshCurrentToken(): Promise<CoreApiToken> {
        const currentToken = this.getToken();
        if (!currentToken) {
            throw new Error('NO_TOKEN_TO_REFRESH');
        }
        return this._coreApiAuthService.switchProjectInToken(currentToken.project_id).then(token => {
            this.registerToken(token, this.readOrCreateSessionId());
            return token;
        });
    }

    resetPassword(email: string, branding: string): Promise<void> {
        return this._coreApiAuthService.resetPassword(email, branding);
    }

    private reloginAs(token: CoreApiToken, email: string): Promise<CoreApiToken> {
        return this._coreApiAuthService
            .getTokenForConnectedAsFromSpecificToken(
                token.access_token,
                email,
                localStorage.getItem(RosaCoreLocalStorageKey.CONNECTED_AS_PROJECT_ID) as string
            )
            .then(token => {
                this.registerConnectedAs(email);
                return token;
            })
            .catch(error => {
                this._store.dispatch(new RosaAuthStoreActions.LoginFailed());
                throw error;
            });
    }
}
